<template>
  <b-modal
    :id="modalId"
    :visible="visible"
    size="lg"
    centered
    hide-footer
    hide-header
    modal-class="glassmorphism-modal"
    content-class="glass-content"
    @hidden="$emit('close')"
  >
    <!-- Branded Header -->
    <div class="modal-branded-header">
      <div class="header-content">
        <div class="header-icon">
          <feather-icon
            icon="CalendarIcon"
            size="22"
          />
        </div>
        <div class="header-text">
          <h5 class="modal-title">
            {{ $t('DEBTIA_PAYMENT_PLAN') }}
          </h5>
          <div class="modal-subtitle">
            <span class="creditor">{{ caseData.creditorName }}</span>
            <span class="separator">&middot;</span>
            <span class="journal">{{ $t('DEBTIA_CASE_PREFIX') }} {{ caseData.journalNumber }}</span>
          </div>
        </div>
      </div>
      <button
        type="button"
        class="close-btn"
        @click="$emit('close')"
      >
        <feather-icon
          icon="XIcon"
          size="18"
        />
      </button>
    </div>

    <div class="payment-plan-content">
      <!-- Next Payment Callout -->
      <div
        v-if="nextPaymentDate"
        class="next-payment-callout"
      >
        <div class="callout-icon">
          <feather-icon
            icon="ClockIcon"
            size="20"
          />
        </div>
        <div class="callout-info">
          <span class="callout-label">{{ $t('DEBTIA_NEXT_PAYMENT') }}</span>
          <span class="callout-date">{{ nextPaymentDate }}</span>
        </div>
        <div class="callout-amount">
          {{ nextPaymentAmountFormatted }}
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="summary-grid">
        <div class="summary-card">
          <span class="summary-icon">
            <feather-icon
              icon="DollarSignIcon"
              size="16"
            />
          </span>
          <span class="summary-label">{{ $t('DEBTIA_TOTAL_AMOUNT') }}</span>
          <span class="summary-value">{{ formatCurrency(caseData.totalAmount) }}</span>
        </div>
        <div class="summary-card">
          <span class="summary-icon">
            <feather-icon
              icon="LayersIcon"
              size="16"
            />
          </span>
          <span class="summary-label">{{ $t('DEBTIA_NUMBER_OF_INSTALLMENTS') }}</span>
          <span class="summary-value">{{ totalInstallments }}</span>
        </div>
        <div class="summary-card">
          <span class="summary-icon">
            <feather-icon
              icon="RepeatIcon"
              size="16"
            />
          </span>
          <span class="summary-label">{{ $t('DEBTIA_PER_INSTALLMENT') }}</span>
          <span class="summary-value">{{ formatCurrency(installmentAmount) }}</span>
        </div>
      </div>

      <!-- Progress -->
      <div
        v-if="totalInstallments > 0"
        class="plan-progress"
      >
        <div class="progress-header">
          <span class="progress-label">{{ $t('DEBTIA_PROGRESS') }}</span>
          <span class="progress-count">
            <strong>{{ paidCount }}</strong> {{ $t('DEBTIA_INSTALLMENTS_OF') }} <strong>{{ totalInstallments }}</strong> {{ $t('DEBTIA_INSTALLMENTS_PAID_LABEL') }}
          </span>
        </div>
        <div class="progress-track">
          <div
            class="progress-fill"
            :style="{ width: progressPercent + '%' }"
          />
        </div>
        <div class="progress-percent">
          {{ progressPercent }}%
        </div>
      </div>

      <!-- Installments List -->
      <div class="installments-list">
        <div class="list-header">
          <span class="col-nr">{{ $t('DEBTIA_COL_NR') }}</span>
          <span class="col-date">{{ $t('DEBTIA_COL_DUE_DATE') }}</span>
          <span class="col-amount">{{ $t('DEBTIA_COL_INSTALLMENT') }}</span>
          <span class="col-remaining">{{ $t('DEBTIA_COL_REMAINING') }}</span>
          <span class="col-status">Status</span>
        </div>
        <div
          v-for="item in installments"
          :key="item.rate_number"
          class="list-row"
          :class="'row-' + item.status"
        >
          <span class="col-nr">{{ item.rate_number }}</span>
          <span class="col-date">{{ item.due_date }}</span>
          <span class="col-amount">{{ formatCurrency(item.amount) }}</span>
          <span class="col-remaining">{{ formatCurrency(item.remaining_amount) }}</span>
          <span class="col-status">
            <span
              class="status-pill"
              :class="'pill-' + item.status"
            >
              <feather-icon
                :icon="getStatusIcon(item.status)"
                size="12"
                class="pill-icon"
              />
              {{ getStatusText(item.status) }}
            </span>
          </span>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer-actions">
        <b-button
          variant="flat-secondary"
          class="close-action"
          @click="$emit('close')"
        >
          {{ $t('DEBTIA_CLOSE') }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal, BButton,
} from 'bootstrap-vue'

export default {
  name: 'PaymentPlanModal',
  components: {
    BModal,
    BButton,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    caseData: {
      type: Object,
      default: () => ({}),
    },
    installments: {
      type: Array,
      default: () => [],
    },
    planData: {
      type: Object,
      default: () => null,
    },
    modalId: {
      type: String,
      default: 'payment-plan-modal',
    },
  },
  computed: {
    totalInstallments() {
      if (this.planData) return this.planData.total_installments || 0
      return this.installments.length
    },
    installmentAmount() {
      if (this.installments.length === 0) return 0
      return this.installments[0]?.amount || 0
    },
    paidCount() {
      return this.installments.filter(i => i.status === 'paid').length
    },
    progressPercent() {
      if (this.totalInstallments === 0) return 0
      return Math.round((this.paidCount / this.totalInstallments) * 100)
    },
    nextPaymentDate() {
      if (!this.planData) return null
      const date = this.planData.next_unpaid_installment_date
      if (!date) return null
      const [year, month, day] = date.split('-')
      return `${day}. ${this.getMonthName(parseInt(month, 10))} ${year}`
    },
    nextPaymentAmountFormatted() {
      if (!this.planData) return null
      const amount = this.planData.next_unpaid_installment_amount
      if (!amount) return null
      // If already a string like "3427,58 kr.", return as-is
      if (typeof amount === 'string') return amount
      return this.formatCurrency(amount)
    },
  },
  methods: {
    getMonthName(month) {
      const months = [
        '', 'januar', 'februar', 'marts', 'april', 'maj', 'juni',
        'juli', 'august', 'september', 'oktober', 'november', 'december',
      ]
      return months[month] || ''
    },
    formatCurrency(value) {
      if (!value && value !== 0) return '—'
      return new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: 'DKK',
        minimumFractionDigits: 2,
      }).format(value)
    },
    getStatusIcon(status) {
      const icons = {
        paid: 'CheckCircleIcon',
        pending: 'ClockIcon',
        overdue: 'AlertCircleIcon',
      }
      return icons[status] || 'MinusCircleIcon'
    },
    getStatusText(status) {
      const texts = {
        paid: this.$t('DEBTIA_STATUS_PAID'),
        pending: this.$t('DEBTIA_STATUS_PENDING'),
        overdue: this.$t('DEBTIA_STATUS_OVERDUE'),
      }
      return texts[status] || status
    },
  },
}
</script>

<style lang="scss">
// Unscoped: glassmorphism backdrop + modal overrides
.glassmorphism-modal {
  .modal-backdrop,
  &.modal {
    // handled by bootstrap
  }

  .modal-dialog {
    max-width: 680px;
  }

  .glass-content {
    background: rgba(255, 255, 255, 0.92) !important;
    backdrop-filter: blur(24px) saturate(1.4);
    -webkit-backdrop-filter: blur(24px) saturate(1.4);
    border: 1px solid rgba(255, 255, 255, 0.5) !important;
    border-radius: 1.25rem !important;
    box-shadow:
      0 8px 32px rgba(0, 41, 84, 0.12),
      0 2px 8px rgba(0, 0, 0, 0.06),
      inset 0 1px 0 rgba(255, 255, 255, 0.6) !important;
    overflow: hidden;
    padding: 0 !important;
  }

  .modal-body {
    padding: 0 !important;
  }

  // Backdrop blur
  &.modal .modal-backdrop {
    backdrop-filter: blur(8px);
  }
}
</style>

<style lang="scss" scoped>
.modal-branded-header {
  background:
    radial-gradient(ellipse 70% 200% at 10% 10%, #088c76 0%, transparent 60%),
    radial-gradient(ellipse 100% 140% at 80% 30%, #002954 0%, transparent 100%);
  background-color: #000;
  padding: 1.5rem 1.75rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  .header-content {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .header-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    flex-shrink: 0;
  }

  .header-text {
    .modal-title {
      font-size: 1.2rem;
      font-weight: 700;
      color: #fff;
      margin: 0 0 0.15rem;
      letter-spacing: -0.01em;
    }

    .modal-subtitle {
      font-size: 0.82rem;
      color: rgba(255, 255, 255, 0.7);

      .separator {
        margin: 0 0.4rem;
        opacity: 0.5;
      }

      .creditor {
        font-weight: 500;
        color: rgba(255, 255, 255, 0.85);
      }
    }
  }

  .close-btn {
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    padding: 6px;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.25);
      color: #fff;
    }
  }
}

.payment-plan-content {
  padding: 1.5rem 1.75rem;

  // Next Payment Callout
  .next-payment-callout {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.25rem;
    margin-bottom: 1.25rem;
    background: linear-gradient(135deg, rgba(8, 140, 118, 0.08) 0%, rgba(0, 41, 84, 0.06) 100%);
    border: 1px solid rgba(8, 140, 118, 0.15);
    border-radius: 0.875rem;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: linear-gradient(to bottom, #088c76, #002954);
      border-radius: 4px 0 0 4px;
    }

    .callout-icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      background: linear-gradient(135deg, #088c76, #0a6e5e);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      flex-shrink: 0;
      box-shadow: 0 2px 8px rgba(8, 140, 118, 0.3);
    }

    .callout-info {
      flex: 1;

      .callout-label {
        display: block;
        font-size: 0.72rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #6e6b7b;
        margin-bottom: 0.15rem;
      }

      .callout-date {
        font-size: 0.95rem;
        font-weight: 600;
        color: #002954;
      }
    }

    .callout-amount {
      font-size: 1.15rem;
      font-weight: 700;
      color: #088c76;
      white-space: nowrap;
    }
  }

  // Summary Cards Grid
  .summary-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
    margin-bottom: 1.5rem;

    .summary-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      padding: 1rem 0.75rem;
      background: rgba(0, 0, 0, 0.02);
      border: 1px solid rgba(0, 0, 0, 0.05);
      border-radius: 0.75rem;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(0, 0, 0, 0.035);
        border-color: rgba(0, 0, 0, 0.08);
      }

      .summary-icon {
        width: 32px;
        height: 32px;
        border-radius: 8px;
        background: rgba(8, 140, 118, 0.1);
        color: #088c76;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 0.6rem;
      }

      .summary-label {
        display: block;
        font-size: 0.72rem;
        font-weight: 500;
        color: #a0a0a0;
        margin-bottom: 0.3rem;
        text-transform: uppercase;
        letter-spacing: 0.03em;
      }

      .summary-value {
        font-size: 1.05rem;
        font-weight: 700;
        color: #002954;
      }
    }
  }

  // Progress Section
  .plan-progress {
    margin-bottom: 1.5rem;
    padding: 1rem 1.25rem;
    background: rgba(0, 0, 0, 0.02);
    border-radius: 0.75rem;
    border: 1px solid rgba(0, 0, 0, 0.04);

    .progress-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.6rem;

      .progress-label {
        font-size: 0.82rem;
        font-weight: 600;
        color: #5e5873;
      }

      .progress-count {
        font-size: 0.78rem;
        color: #6e6b7b;

        strong {
          color: #002954;
        }
      }
    }

    .progress-track {
      height: 8px;
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.06);
      overflow: hidden;
      margin-bottom: 0.4rem;

      .progress-fill {
        height: 100%;
        border-radius: 4px;
        background: linear-gradient(90deg, #088c76, #28c76f);
        transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;

        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.3) 50%,
            transparent 100%
          );
        }
      }
    }

    .progress-percent {
      font-size: 0.72rem;
      font-weight: 600;
      color: #088c76;
      text-align: right;
    }
  }

  // Installments List (custom, no b-table)
  .installments-list {
    margin-bottom: 1.25rem;
    border-radius: 0.75rem;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.06);

    .list-header {
      display: flex;
      align-items: center;
      padding: 0.7rem 1rem;
      background: rgba(0, 41, 84, 0.04);
      font-size: 0.72rem;
      font-weight: 600;
      color: #6e6b7b;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    }

    .list-row {
      display: flex;
      align-items: center;
      padding: 0.65rem 1rem;
      font-size: 0.84rem;
      color: #5e5873;
      border-bottom: 1px solid rgba(0, 0, 0, 0.04);
      transition: background 0.15s ease;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background: rgba(0, 0, 0, 0.015);
      }

      &.row-paid {
        .col-amount,
        .col-remaining {
          color: #a0a0a0;
        }
      }

      &.row-overdue {
        background: rgba(234, 84, 85, 0.03);

        &:hover {
          background: rgba(234, 84, 85, 0.05);
        }
      }
    }

    .col-nr {
      width: 48px;
      flex-shrink: 0;
      font-weight: 500;
      color: #a0a0a0;
    }

    .col-date {
      flex: 1.2;
      min-width: 0;
    }

    .col-amount {
      flex: 1;
      min-width: 0;
      text-align: right;
      font-weight: 500;
    }

    .col-remaining {
      flex: 1;
      min-width: 0;
      text-align: right;
      font-weight: 500;
    }

    .col-status {
      width: 110px;
      flex-shrink: 0;
      text-align: right;
    }

    .status-pill {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 0.25rem 0.6rem;
      border-radius: 20px;
      font-size: 0.72rem;
      font-weight: 600;
      letter-spacing: 0.01em;

      .pill-icon {
        flex-shrink: 0;
      }

      &.pill-paid {
        background: rgba(40, 199, 111, 0.12);
        color: #28c76f;
      }

      &.pill-pending {
        background: rgba(255, 159, 67, 0.12);
        color: #ff9f43;
      }

      &.pill-overdue {
        background: rgba(234, 84, 85, 0.12);
        color: #ea5455;
      }
    }
  }

  // Footer
  .modal-footer-actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.05);

    .close-action {
      font-size: 0.85rem;
      font-weight: 500;
      padding: 0.55rem 1.5rem;
      border-radius: 8px;
      color: #6e6b7b;

      &:hover {
        background: rgba(0, 0, 0, 0.04);
        color: #5e5873;
      }
    }
  }
}

// Responsive - Tablet
@media (max-width: 992px) {
  .payment-plan-content {
    .summary-grid {
      grid-template-columns: 1fr 1fr;
    }

    .installments-list {
      .col-remaining {
        display: none;
      }
    }
  }
}

// Responsive - Mobile
@media (max-width: 576px) {
  .payment-plan-content {
    padding: 1.25rem;

    .next-payment-callout {
      flex-wrap: wrap;

      .callout-amount {
        width: 100%;
        text-align: left;
        padding-left: 52px;
        margin-top: -0.25rem;
      }
    }

    .summary-grid {
      grid-template-columns: 1fr;
    }

    .installments-list {
      .col-remaining {
        display: none;
      }

      .col-status {
        width: 90px;
      }
    }
  }
}
</style>
